<template>
  <TheObserver
    :handle-intersect="handleIntersect"
    :handle-leave="handleLeave"
    :root-margin="`-${headerHeight}px 0px 0px 0px`"
  />
  <div
    class="left-0 z-page-navigation w-full"
    :class="isFixed && 'sticky'"
    :style="{ top: `${headerHeight + 1}px` }"
  >
    <div class="relative w-full" :style="{ height: `${navigationHeight}px` }">
      <nav class="container-wrap relative py-2.5 text-center">
        <template v-if="!isMobile">
          <div
            class="inline-flex max-w-full items-stretch gap-2 rounded-xl border border-solid border-gray-400 bg-gray-300 p-2 shadow-navigation"
            :style="{ height: `${navigationHeight - 20}px` }"
          >
            <ul
              ref="scrollRef"
              class="scrollbar-h-px inline-flex min-w-0 cursor-move items-center gap-2 overflow-y-hidden overflow-x-scroll"
            >
              <li v-for="item in navigation" :key="item.id" class="shrink-0">
                <button
                  type="button"
                  :data-nav-id="item.id"
                  class="rounded-xl border border-solid p-3 text-black"
                  :class="
                    item.id === activeId
                      ? 'border-gray-400 bg-white'
                      : 'border-transparent bg-transparent hover:bg-gray-600_a25'
                  "
                  :title="item.name"
                  @click="handleScrollTo(item.id, offset)"
                >
                  <span class="truncate text-base font-bold">
                    {{ item.name }}
                  </span>
                </button>
              </li>
            </ul>

            <ul
              v-if="links.length > 0"
              class="inline-flex shrink-0 items-center gap-2"
            >
              <li v-for="item in links" :key="item.link" class="shrink-0">
                <TheButton
                  :id="item.trackingCode"
                  :to="item.link"
                  :title="item.name"
                  class="group inline-flex items-center"
                >
                  <span class="truncate text-sm font-bold tablet:text-base">
                    {{ item.name }}
                  </span>
                </TheButton>
              </li>
            </ul>
          </div>

          <button
            v-if="isDesktop && canScroll"
            class="absolute left-0 top-2/4 z-50 flex h-16 w-16 -translate-x-20 -translate-y-2/4 items-center justify-center rounded-full bg-gray-600_a15 text-black transition-all hover:bg-gray-600_a25"
            :class="arrivedState.left && 'pointer-events-none opacity-50'"
            @click="handleScrollToEdge('left')"
          >
            <IconCaret direction="left" />
          </button>

          <button
            v-if="isDesktop && canScroll"
            class="absolute right-0 top-2/4 z-50 flex h-16 w-16 -translate-y-2/4 translate-x-20 items-center justify-center rounded-full bg-gray-600_a15 text-black transition-all hover:bg-gray-600_a25"
            :class="arrivedState.right && 'pointer-events-none opacity-50'"
            @click="handleScrollToEdge('right')"
          >
            <IconCaret direction="right" />
          </button>
        </template>
        <template v-else>
          <div ref="mobileNavRef" class="relative w-full">
            <div
              class="flex w-full items-center rounded-xl border border-solid border-gray-400 bg-gray-300 p-2 shadow-navigation"
              :style="{ height: `${navigationHeight - 20}px` }"
            >
              <button
                type="button"
                class="relative flex flex-1 items-center px-2"
                @click="isMobileListOpen = !isMobileListOpen"
              >
                <p class="flex-1 truncate pr-1 text-left font-bold">
                  {{ activeName }}
                </p>
                <IconCaret
                  :direction="isMobileListOpen ? 'up' : 'down'"
                  class="pointer-events-none h-6 w-6 flex-shrink-0 basis-6 text-black"
                />
              </button>

              <TheNuxtLink
                v-if="links && links.length"
                :to="links[0].link"
                :title="links[0].name"
                class="block w-36 flex-shrink-0 basis-36 rounded-2xl bg-black px-3 py-2 leading-4 text-white"
              >
                <span class="truncate">{{ links[0].name }}</span>
              </TheNuxtLink>
            </div>

            <div
              v-if="isMobileListOpen"
              class="absolute left-0 top-full z-50 mt-2 w-full rounded-xl border border-solid border-gray-400 bg-gray-300 p-2 shadow-navigation"
            >
              <ul class="max-h-80 w-full overflow-hidden overflow-y-auto">
                <li v-for="item in navigation" :key="item.id">
                  <button
                    type="button"
                    class="w-full truncate rounded-xl p-3 text-left text-black"
                    :class="
                      item.id === activeId
                        ? 'bg-white font-bold'
                        : 'hover:bg-gray-600_a25'
                    "
                    :title="item.name"
                    @click="handleMobileNavClick(item.id)"
                  >
                    {{ item.name }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onClickOutside, useScroll, useWindowSize } from '@vueuse/core';
  import ScrollBooster from 'scrollbooster';

  interface ILink {
    name: string;
    link: string;
    trackingCode?: string;
    hasArrow?: boolean;
    handler?: () => void;
  }

  interface IProps {
    triggerId: string;
    navigation: { id: string; name: string }[];
    links?: ILink[];
  }

  const props = withDefaults(defineProps<IProps>(), {
    triggerId: '',
    navigation: () => [],
    links: () => [],
  });

  const { width } = useWindowSize();
  const { isMobile, isDesktop } = useScreen();
  const { headerHeight, navigationHeight } = useWebsite();

  const isFixed = ref<boolean>(false);

  const scrollRef = ref<HTMLUListElement | null>(null);

  let scrollBooster: ScrollBooster | null = null;

  const { x: scrollX, arrivedState } = useScroll(scrollRef, {
    behavior: 'smooth',
  });

  const canScroll = ref<boolean>(false);

  const mobileNavRef = ref<HTMLElement | null>(null);
  const isMobileListOpen = ref<boolean>(false);

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const { handleScrollTo } = useScrollTo({
    offset: offset.value,
  });

  const activeId = computed((): string => {
    if (props.triggerId) {
      return props.triggerId;
    }
    if (props.navigation.length > 0) {
      return props.navigation[0].id;
    }
    return '';
  });

  const activeName = computed(
    (): string =>
      props.navigation.find((item) => item.id === activeId.value)?.name ?? ''
  );

  const handleLeave = (): void => {
    isFixed.value = true;
  };

  const handleIntersect = (): void => {
    isFixed.value = false;
  };

  const checkCanScroll = (): void => {
    nextTick(() => {
      if (scrollRef.value) {
        canScroll.value =
          scrollRef.value.scrollWidth > scrollRef.value.clientWidth;
      }
    });
  };

  const handleScrollToEdge = (direction: 'left' | 'right'): void => {
    if (!scrollRef.value) return;
    scrollX.value = direction === 'left' ? 0 : scrollRef.value.scrollWidth;
  };

  const handleMobileNavClick = (id: string): void => {
    handleScrollTo(id, offset.value);
    isMobileListOpen.value = false;
  };

  watch(width, () => checkCanScroll());

  watch(
    () => [props.navigation, props.links],
    () => checkCanScroll(),
    {
      deep: true,
    }
  );

  onClickOutside(mobileNavRef, () => {
    isMobileListOpen.value = false;
  });

  onMounted(() => {
    nextTick(() => {
      if (scrollRef.value) {
        scrollBooster = new ScrollBooster({
          viewport: scrollRef.value,
          content: scrollRef.value,
          scrollMode: 'native',
          direction: 'horizontal',
        });
      }
      checkCanScroll();
    });
  });

  onBeforeUnmount(() => {
    scrollBooster?.destroy();
  });

  export type { ILink };
</script>
